<script lang="ts" setup>
import projectStore from '@/store/ProjectStore.ts'
import formStore from '@/store/FormStore.ts'
import { StepsController } from '@/controllers/StepsController.ts'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import SelectionList from '@/components/selection-list/SelectionList.vue'
import SelectionListItem from '@/components/selection-list/SelectionListItem.vue'
import RadioButton from '@/components/RadioButton.vue'
import { api } from '@/services/ApiService.ts'
import { BufferStrategy, BusEvent, CharacteristicType, HpStep, ProductType } from '@/helper/Types.ts'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import i18n from '@/helper/Translations.ts'
import FormController from '@/controllers/FormController.ts'
import SpinnerInput from '@/components/steps/accessories/SpinnerInput.vue'
import { SystemController } from '@/services/models/implementations/SystemController.ts'
import CheckBox from '@/components/CheckBox.vue'
import bus from '@/helper/Bus.ts'
import { controllerExtensionPreselection } from '@/helper/ControllerExtensionPreselection.ts'
import { Product } from '@/services/models/implementations/Product.ts'
import { ControllerExtensionCharacteristic } from '@/services/models/ControllerExtensionCharacteristic.ts'
import { ControllerExtensionModel } from '@/models/ControllerExtensionModel.ts'
import DetailsButton from '@/components/DetailsButton.vue'
import { RemoteControlCharacteristic } from '@/services/models/RemoteControlCharacteristic.ts'
import { Article } from '@/services/models/Article.ts'

const suggestedSystemControllers = ref<ProductResponse[]>([])
const combinableInternetGateways = ref<ProductResponse[]>([])
const combinableControllerExtensions = ref<Article[]>([])
const combinableRemoteControl = ref<ProductResponse>()
const shouldInternetGatewayBeVisible = ref(false)

const showDetailsForId = ref<string>('')

onMounted(async () => {
  bus.$onC(HpStep.SYSTEM_CONTROLLERS, BusEvent.LANGUAGE_CHANGED, async () => {
    await initProductLoadingAndPreselection()
  })

  await initProductLoadingAndPreselection()
})

onUnmounted((): void => {
  bus.$offC(HpStep.SYSTEM_CONTROLLERS)
})

watch(() => formStore.data.systemControllerId, async () => {
  await loadCombinableRemoteControl()
  await loadCombinableControllerExtensions()
})

watch(() => formStore.data.remoteControl?.id + formStore.data.remoteControl?.quantity, async () => {
  await loadCombinableControllerExtensions()
})

async function initProductLoadingAndPreselection(): Promise<void> {
  await loadCombinableInternetGateways()
  preselectInternetGateway()

  await loadSuggestedSystemControllers()
  preselectSystemController()

  FormController.captureInitialData()

  await loadCombinableRemoteControl()
  await loadCombinableControllerExtensions()

  // Re-capture initial data to avoid triggering change detection due to pre-selection for system controller
  FormController.captureInitialData()
  await StepsController.currentStepValid()
  formStore.forceSave = true
}

function preselectSystemController(): void {
  if (!formStore.data.systemControllerId && suggestedSystemControllers.value.length > 0) {
    formStore.data.systemControllerId = suggestedSystemControllers.value[0].id
  }
}

function preselectInternetGateway(): void {
  if (!formStore.data.internetGatewayId && combinableInternetGateways.value.length > 0) {
    formStore.data.internetGatewayId = combinableInternetGateways.value[0].id
  }
}

async function loadSuggestedSystemControllers(): Promise<void> {
  suggestedSystemControllers.value = (await api.loadProducts(projectStore.projectId, ProductType.SYSTEM_CONTROLLER))

  const systemControllerSaleSet = combinableInternetGateways.value.find(systemControllerSaleSet => systemControllerSaleSet.inSalePackage === true)

  if (systemControllerSaleSet) {
    new SystemController(systemControllerSaleSet)
  } else {
    suggestedSystemControllers.value.map(p => new SystemController(p))
  }

}

async function loadCombinableInternetGateways(): Promise<void> {
  combinableInternetGateways.value = await api.loadCombinables(CharacteristicType.INTERNET_GATEWAY)

  const internetGatewaysSaleSet = combinableInternetGateways.value.find(internetGatewaysSaleSet => internetGatewaysSaleSet.inSalePackage === true)

  if (internetGatewaysSaleSet) {
    combinableInternetGateways.value = [internetGatewaysSaleSet]
    shouldInternetGatewayBeVisible.value = true
  } else {
    // Add none option to radio group
    combinableInternetGateways.value.unshift({
      characteristics: [],
      id: '',
      marketingName: '',
      name: i18n.global.t('step_system_controllers.internet_gateway_not_necessary'),
      normalizedMarketingName: '',
      orderNumber: '',
      pictureUrl: 'none',
      price: 0,
      inSalePackage: false,
      productAdvantages: []
    })
  }
}

async function loadCombinableRemoteControl(): Promise<void> {
  const combinableRemoteControls = await api.loadCombinables(CharacteristicType.REMOTE_CONTROL, formStore.data.systemControllerId as string)

  if (combinableRemoteControls.length === 0) {
    formStore.data.remoteControl = {}
    combinableRemoteControl.value = undefined
    return
  }

  if (combinableRemoteControls?.length > 0) {
    combinableRemoteControl.value = (combinableRemoteControls[0])
  }

  let article = new Article({
    id: combinableRemoteControl.value?.id,
    details: new Product(combinableRemoteControl.value),
    quantity: getRemoteControlPreselectedQuantity()
  })

  // Don't pre-select remote control if system controller is already saved
  if (!projectStore.projectProperties.remoteControl.val?.id && projectStore.projectProperties.systemControllerId.val) {
    formStore.data.remoteControl = {
      ...article,
      quantity: 0
    }
    return
  }

  if (combinableRemoteControl.value && formStore.data.remoteControl.id !== combinableRemoteControl.value.id) {
    formStore.data.remoteControl = article
  }
}

async function loadCombinableControllerExtensions(): Promise<void> {
  combinableControllerExtensions.value = (await api.loadCombinables(CharacteristicType.CONTROLLER_EXTENSION))
    .map(ce => {
      const controllerExtension = new Product(ce)

      return new Article({
        id: controllerExtension.id,
        details: controllerExtension,
        quantity: getControllerExtensionQuantity(controllerExtension)
      })
    })

  formStore.data.controllerExtensions = combinableControllerExtensions.value
}

function getSelectedRemoteControl(): RemoteControlCharacteristic | undefined {
  if (formStore.data.remoteControl === undefined) {
    return
  }

  if (formStore.data.remoteControl.quantity === 0) {
    return
  }

  return new Product(formStore.data.remoteControl.details)
    .getProductCharacteristic<RemoteControlCharacteristic>(CharacteristicType.REMOTE_CONTROL)
}

function getControllerExtensionQuantity(controllerExtension: Product): number {
  const savedControllerExtension = (projectStore.projectProperties.controllerExtensions.val || [])
    .find((c: Article) => c.id === controllerExtension.id)

  const projectStoreRemoteControl = projectStore.projectProperties.remoteControl.val
  const formStoreRemoteControl = formStore.data.remoteControl

  const remoteControlChanged = (formStoreRemoteControl?.id !== projectStoreRemoteControl?.id || formStoreRemoteControl?.quantity !== projectStoreRemoteControl?.quantity)
    && !(projectStoreRemoteControl?.quantity === undefined && formStoreRemoteControl?.quantity === 0)

  const systemControllerChanged = formStore.data.systemControllerId !== projectStore.projectProperties.systemControllerId.val

  if (projectStore.projectProperties.systemControllerId.val && !savedControllerExtension && !remoteControlChanged && !systemControllerChanged) {
    return 0
  }

  if (savedControllerExtension && !remoteControlChanged) {
    return savedControllerExtension.quantity
  } else {
    return shouldPreselectControllerExtension(controllerExtension) ? 1 : 0
  }
}

function shouldPreselectControllerExtension(controllerExtension: Product): boolean {
  return controllerExtensionPreselection.shouldPreselect(
    controllerExtension,
    {
      decouplingBuffer: projectStore.projectProperties.bufferStrategy.val === BufferStrategy.DECOUPLING,
      amountOfCircuits: projectStore.projectProperties.circuits.val?.length || 0,
      remoteControl: getSelectedRemoteControl(),
      heatPumpCascadeSize: projectStore.projectProperties.heatPumpCascadeSize.val,
    },
    combinableControllerExtensions.value.length
  )
}

function getControllerExtensionModel(controllerExtension: Article) {
  return controllerExtension.details?.getProductCharacteristic<ControllerExtensionCharacteristic>(CharacteristicType.CONTROLLER_EXTENSION).model
}

function toggleControllerExtension(controllerExtension: Article) {
  if (!controllerExtension.details) {
    return
  }

  if (shouldPreselectControllerExtension(controllerExtension.details) && getControllerExtensionModel(controllerExtension) === ControllerExtensionModel.VR71) {
    return
  }

  controllerExtension.quantity = controllerExtension.quantity > 0 ? 0 : 1
}

function showProductDetails(productId: string): void {
  showDetailsForId.value = showDetailsForId.value === productId ? '' : productId
}

function toggleRemoteControl() {
  if (formStore.data.remoteControl.quantity > 0) {
    formStore.data.remoteControl.quantity = 0
  } else {
    const remoteControlPreselectedQuantity = getRemoteControlPreselectedQuantity()
    formStore.data.remoteControl.quantity = remoteControlPreselectedQuantity > 0
      ? remoteControlPreselectedQuantity
      : 1
  }
}

function getRemoteControlPreselectedQuantity() {
  const circuitCount = projectStore.projectProperties.circuits.val?.length || 0

  return circuitCount - 1
}

function validate(): boolean {
  return !!formStore.data.systemControllerId
}

defineExpose({
  validate
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <form id="system-contoller" class="form steps__form card" @submit.prevent="StepsController.nextClicked()">
      <section v-if="combinableInternetGateways.length > 1 || shouldInternetGatewayBeVisible" id="internet-gateways">
        <div class="section__headline">
          <SectionHeadline translation-key="step_system_controllers.internet_gateways_section_title" />
          <p>{{ $t('step_system_controllers.internet_gateways_section_description') }}</p>
        </div>

        <SelectionList>
          <SelectionListItem
            v-for="internetGateway in combinableInternetGateways"
            :key="internetGateway.id"
            :for-input="internetGateway.name"
            :product="internetGateway"
            :show-details="showDetailsForId === internetGateway.id"
            @click="formStore.data.internetGatewayId = internetGateway.id"
          >
            <template #input>
              <RadioButton
                :id="internetGateway.name"
                v-model="formStore.data.internetGatewayId"
                :value="internetGateway.id"
                name="internetGateway"
              />
            </template>

            <template v-if="internetGateway.productAdvantages?.length > 0" #details-btn>
              <DetailsButton
                :is-open="showDetailsForId === internetGateway.id"
                @click.prevent.stop="showProductDetails(internetGateway.id)"
              />
            </template>
          </SelectionListItem>
        </SelectionList>
      </section>

      <section v-if="suggestedSystemControllers.length > 0" id="system-controllers">
        <div class="section__headline">
          <SectionHeadline translation-key="step_system_controllers.section_title" />
          <p>{{ $t('step_system_controllers.section_description') }}</p>
        </div>

        <SelectionList>
          <SelectionListItem
            v-for="systemController in suggestedSystemControllers"
            :key="systemController.id"
            :for-input="systemController.name"
            :product="systemController"
            :show-details="showDetailsForId === systemController.id"
            @click="formStore.data.systemControllerId = systemController.id"
          >
            <template #input>
              <RadioButton
                :id="systemController.name"
                v-model="formStore.data.systemControllerId"
                :value="systemController.id"
                name="systemController"
              />
            </template>

            <template v-if="systemController.productAdvantages?.length > 0" #details-btn>
              <DetailsButton
                :is-open="showDetailsForId === systemController.id"
                @click.prevent.stop="showProductDetails(systemController.id)"
              />
            </template>
          </SelectionListItem>
        </SelectionList>
      </section>

      <section v-if="combinableRemoteControl?.id" id="remote-controls">
        <div class="section__headline">
          <SectionHeadline translation-key="step_system_controllers.remote_controls_section_title" />
          <p>{{ $t('step_system_controllers.remote_controls_section_description') }}</p>
        </div>

        <SelectionList>
          <SelectionListItem
            :product="combinableRemoteControl"
            :show-details="showDetailsForId === combinableRemoteControl.id"
            @click="toggleRemoteControl()"
          >
            <template #input>
              <CheckBox
                :id="combinableRemoteControl.name"
                :model-value="formStore.data.remoteControl.quantity > 0"
                name="remoteControl"
                @update:model-value="toggleRemoteControl()"
              />
            </template>

            <template v-if="combinableRemoteControl.productAdvantages?.length > 0" #details-btn>
              <DetailsButton
                :is-open="showDetailsForId === combinableRemoteControl.id"
                @click.prevent.stop="showProductDetails(combinableRemoteControl.id)"
              />
            </template>

            <template #quantity-input>
              <div class="fTable__td column is--spinner-controls">
                <SpinnerInput
                  :id="`remoteControl-${combinableRemoteControl.id}`"
                  v-model="formStore.data.remoteControl.quantity"
                  :hide-controls-mobile="true"
                  :max-val="(projectStore.projectProperties.circuits.val?.length || 0)"
                  :min-val="0"
                />
              </div>
            </template>
          </SelectionListItem>
        </SelectionList>
      </section>

      <section v-if="combinableControllerExtensions.length > 0" id="controller-extensions">
        <div class="section__headline">
          <SectionHeadline translation-key="step_system_controllers.controller_extensions_section_title" />
          <p>{{ $t('step_system_controllers.controller_extensions_section_description') }}</p>
        </div>

        <SelectionList>
          <template v-for="controllerExtension in formStore.data.controllerExtensions">
            <SelectionListItem
              v-if="controllerExtension.details"
              :key="controllerExtension.id"
              :for-input="controllerExtension.details.name"
              :product="controllerExtension.details"
              :show-details="showDetailsForId === controllerExtension.id"
              @click="toggleControllerExtension(controllerExtension)"
            >
              <template #input>
                <CheckBox
                  :id="controllerExtension.details.name"
                  :disabled="shouldPreselectControllerExtension(controllerExtension.details)
                    && getControllerExtensionModel(controllerExtension) === ControllerExtensionModel.VR71"
                  :model-value="controllerExtension.quantity > 0"
                  name="controllerExtensionCheckBox"
                  @update:model-value="toggleControllerExtension(controllerExtension)"
                />
              </template>

              <template v-if="controllerExtension.details.productAdvantages?.length > 0" #details-btn>
                <DetailsButton
                  :is-open="showDetailsForId === controllerExtension.id"
                  @click.prevent.stop="showProductDetails(controllerExtension.id)"
                />
              </template>

              <template
                v-if="getControllerExtensionModel(controllerExtension) === ControllerExtensionModel.VR70"
                #quantity-input
              >
                <div class="fTable__td column is--spinner-controls">
                  <SpinnerInput
                    :id="`controllerExtension-${controllerExtension.id}`"
                    v-model="controllerExtension.quantity"
                    :hide-controls-mobile="true"
                    :max-val="3"
                    :min-val="0"
                  />
                </div>
              </template>
            </SelectionListItem>
          </template>
        </SelectionList>
      </section>

      <input class="is--invisible" type="submit">
    </form>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

form {
  max-width: unset;

  section {
    padding: rem-calc(32);

    @include media('<tablet') {
      padding: 1rem;
    }

    &:not(:last-of-type) {
      border-bottom: 1px solid $borderColorLight;
    }

    .section__headline {
      margin-bottom: 2rem;
    }
  }

  .fTable.is--products-table .column.is--spinner-controls {
    @include media('<desktop') {
      justify-content: center;
    }
  }
}
</style>
